import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeadSchema, type ClientDto, type EmployeeDto, type LeadDto } from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog } from '../../shared/ui/forms';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import {
  ActiveClientPicker,
  ActiveEmployeePicker,
  ClientRef,
  EmployeeName,
  ListSelect,
  RELEASE_ONE_STAGES,
  STAGE_LABELS,
  useCrmLists,
} from '../crm/common';
import { Facts, RecordFailure } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import {
  CampaignRef,
  LEADS,
  leadHistoryLabels,
  relationOf,
  useLeadLists,
  valueLabel,
} from './common';
import { LeadDialog } from './LeadsPage';
import { EmailLink, PhoneLinks } from '../../shared/ui/contact';

type DialogKind = 'edit' | 'assign' | 'contact' | 'convert' | 'link';

/**
 * One lead (`/marketing/leads/:id`; ERD §7.6, FRD-MKT-005 to MKT-016, MKT-029 to MKT-031): the
 * record with its next action, campaign and client link, and the actions the session may take —
 * edit, reassign, record a contact, convert to a client (CRM creates it) or link to an existing
 * client — with its documents and history.
 */
export function LeadDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const detail = useQuery({
    queryKey: ['marketing', 'lead', id],
    queryFn: () => getData(`${LEADS}/${id}`, LeadSchema),
  });
  const back = (
    <Button component={RouterLink} to="/marketing/leads" startIcon={<ArrowBackIcon />}>
      All leads
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the lead" />;
  if (detail.isError) {
    return <RecordFailure title="Lead" noun="lead" error={detail.error} back={back} />;
  }
  return <LeadRecord lead={detail.data} back={back} created={params.get('created') === '1'} />;
}

function LeadRecord({ lead, back, created }: { lead: LeadDto; back: ReactNode; created: boolean }) {
  const session = useSession();
  const lists = useLeadLists();
  const can = (capability: string) => hasCapability(session.data, capability);
  const canChange = can('marketing.lead:change');
  const related = lead.clientId !== null;
  const canConvert = canChange && !related && can('crm.client:create');
  const canLink = canChange && !related && can('crm.client:view');
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const open = (kind: DialogKind) => (event: { currentTarget: HTMLElement }) => {
    event.currentTarget.focus();
    setDialog(kind);
  };
  const close = () => {
    setDialog(null);
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${lead.code} — ${lead.companyName}`}
        actions={
          <>
            {back}
            {canLink && (
              <Button variant="outlined" onClick={open('link')}>
                Link to existing client
              </Button>
            )}
            {canConvert && (
              <Button variant="outlined" onClick={open('convert')}>
                Convert to client
              </Button>
            )}
            {canChange && (
              <Button variant="outlined" onClick={open('contact')}>
                Record contact
              </Button>
            )}
            {canChange && can('hr.employee:view') && (
              <Button variant="outlined" onClick={open('assign')}>
                Assign
              </Button>
            )}
            {canChange && (
              <Button variant="contained" onClick={open('edit')}>
                Edit
              </Button>
            )}
          </>
        }
      />
      {created && (
        <Alert severity="success" variant="outlined">
          Lead {lead.code} created. The Lead ID was generated by the system.
        </Alert>
      )}
      {related && (
        <Alert severity="info" variant="outlined">
          {lead.conversionDate === null
            ? 'This lead is linked to an existing client. The client record is maintained in CRM.'
            : `This lead was converted to a client on ${lead.conversionDate}. The client record is maintained in CRM.`}
        </Alert>
      )}
      <Section title="Lead">
        <Facts
          label="Lead details"
          facts={[
            ['Lead ID', lead.code],
            ['Company', lead.companyName],
            ['Contact', lead.contactName ?? '—'],
            ['Contact email', <EmailLink key="e" email={lead.contactEmail} />],
            ['Contact phone', <PhoneLinks key="p" phone={lead.contactPhone} />],
            ['Status', valueLabel(lists.data?.status, lead.statusCode)],
            ['Priority', valueLabel(lists.data?.priority, lead.priorityCode)],
            [
              'Qualification',
              valueLabel(lists.data?.qualificationStatus, lead.qualificationStatusCode),
            ],
            ['Source', valueLabel(lists.data?.source, lead.sourceCode)],
            ['Industry', valueLabel(lists.data?.industry, lead.industryCode)],
            ['Interest', lead.interest ?? '—'],
            ['Assigned to', <EmployeeName key="a" id={lead.assignedEmployeeId} />],
            [
              'Campaign',
              <CampaignRef
                key="c"
                id={lead.campaignId}
                code={lead.campaignCode}
                name={lead.campaignName}
              />,
            ],
            ['Recorded', lead.createdAt.slice(0, 10)],
          ]}
        />
      </Section>
      <Section title="Follow-up">
        <Facts
          label="Follow-up"
          facts={[
            ['Last contact', lead.lastContactDate ?? '—'],
            ['Next action', lead.nextAction ?? '—'],
            ['Next action due', lead.nextActionDueDate ?? '—'],
          ]}
        />
      </Section>
      <Section title="Client">
        <Facts
          label="Client link"
          facts={[
            ['Relation', relationOf(lead)],
            [
              'Client',
              lead.clientId === null ? (
                '—'
              ) : lead.clientLabel === null ? (
                'Not visible'
              ) : (
                <ClientRef key="cl" id={lead.clientId} label={lead.clientLabel} />
              ),
            ],
          ]}
        />
      </Section>
      <DocumentsPanel recordType="marketing.lead" recordId={lead.id} canAttach={canChange} />
      <RecordHistory
        recordType="marketing.lead"
        recordId={lead.id}
        labels={leadHistoryLabels(lists.data)}
      />

      {dialog === 'edit' && (
        <LeadDialog key={`${lead.id}:${String(lead.version)}`} lead={lead} onClose={close} />
      )}
      {dialog === 'assign' && <AssignDialog lead={lead} onClose={close} />}
      {dialog === 'contact' && <RecordContactDialog lead={lead} onClose={close} />}
      {dialog === 'convert' && <ConvertDialog lead={lead} onClose={close} />}
      {dialog === 'link' && <LinkClientDialog lead={lead} onClose={close} />}
    </Stack>
  );
}

/** Invalidates everything that shows a lead, after a change to it. */
function useLeadChanged() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['marketing', 'lead'] });
    await queryClient.invalidateQueries({ queryKey: ['marketing', 'leads'] });
    await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
  };
}

/** Reassigns the lead to another active employee (FRD-MKT-009); the new assignee is notified. */
function AssignDialog({ lead, onClose }: { lead: LeadDto; onClose: () => void }) {
  const session = useSession();
  const changed = useLeadChanged();
  const [assignee, setAssignee] = useState<EmployeeDto | null>(null);
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${LEADS}/${lead.id}`,
        { assignedEmployeeId: assignee?.id },
        LeadSchema,
        session.data?.csrfToken ?? '',
        lead.version,
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Assign lead"
      submitLabel="Assign"
      canSubmit={assignee !== null && assignee.id !== lead.assignedEmployeeId}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <ActiveEmployeePicker
        label="Assign to"
        required
        value={assignee}
        onChange={setAssignee}
        helperText="An active employee. They are notified where a notification rule is configured."
      />
    </FormDialog>
  );
}

/**
 * Records a contact with the prospect (FRD-MKT-011): the last contact date and the next action with
 * its due date. An emptied next action is cleared.
 */
function RecordContactDialog({ lead, onClose }: { lead: LeadDto; onClose: () => void }) {
  const session = useSession();
  const changed = useLeadChanged();
  const [lastContactDate, setLastContactDate] = useState(
    businessToday(session.data?.businessTimezone),
  );
  const [nextAction, setNextAction] = useState(lead.nextAction ?? '');
  const [dueDate, setDueDate] = useState(lead.nextActionDueDate ?? '');
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${LEADS}/${lead.id}`,
        {
          lastContactDate,
          nextAction: nextAction.trim() === '' ? null : nextAction.trim(),
          nextActionDueDate: dueDate === '' ? null : dueDate,
        },
        LeadSchema,
        session.data?.csrfToken ?? '',
        lead.version,
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Record contact"
      submitLabel="Record contact"
      canSubmit={lastContactDate !== ''}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Contact date"
        type="date"
        required
        value={lastContactDate}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          setLastContactDate(e.target.value);
        }}
      />
      <TextField
        label="Next action"
        multiline
        minRows={2}
        value={nextAction}
        onChange={(e) => {
          setNextAction(e.target.value);
        }}
      />
      <TextField
        label="Next action due"
        type="date"
        value={dueDate}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          setDueDate(e.target.value);
        }}
        helperText="The assigned employee is reminded when it falls due."
      />
    </FormDialog>
  );
}

/**
 * Converts the lead into a client (FRD-MKT-029, FRD-CRM-047). CRM creates the client and checks it
 * as it checks any new client; the dialog collects what CRM needs that the lead does not carry.
 */
function ConvertDialog({ lead, onClose }: { lead: LeadDto; onClose: () => void }) {
  const session = useSession();
  const changed = useLeadChanged();
  const queryClient = useQueryClient();
  const crmLists = useCrmLists();
  const canAddContact = hasCapability(session.data, 'crm.client_contact:create');
  const [companyName, setCompanyName] = useState(lead.companyName);
  const [stage, setStage] = useState('');
  const [owner, setOwner] = useState<EmployeeDto | null>(null);
  const [industryCode, setIndustryCode] = useState(lead.industryCode ?? '');
  const [clientTypeCode, setClientTypeCode] = useState('');
  const [sourceCode, setSourceCode] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [recordContact, setRecordContact] = useState(lead.contactName !== null && canAddContact);
  const [confirming, setConfirming] = useState(false);

  const body: Record<string, unknown> = {
    companyName: companyName.trim(),
    lifecycleStage: stage,
    industryCode: industryCode === '' ? null : industryCode,
    recordContact,
  };
  if (owner !== null) body.accountOwnerEmployeeId = owner.id;
  if (clientTypeCode !== '') body.clientTypeCode = clientTypeCode;
  if (sourceCode !== '') body.sourceCode = sourceCode;
  if (email.trim() !== '') body.email = email.trim();
  if (phone.trim() !== '') body.phone = phone.trim();

  const save = useMutation({
    mutationFn: () =>
      postJson(`${LEADS}/${lead.id}/convert`, body, LeadSchema, session.data?.csrfToken ?? '', {
        expectedVersion: lead.version,
      }),
    onSuccess: async () => {
      await changed();
      await queryClient.invalidateQueries({ queryKey: ['crm', 'clients'] });
      onClose();
    },
  });

  if (confirming) {
    return (
      <ConfirmDialog
        open
        title="Convert to client"
        message={`Create the client ${companyName.trim()} in CRM from lead ${lead.code}? A lead converts once; the client is then maintained in CRM.`}
        confirmLabel="Convert"
        busy={save.isPending}
        error={save.isError ? save.error.message : null}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          save.mutate();
        }}
      />
    );
  }
  return (
    <FormDialog
      open
      title={`Convert ${lead.code} to a client`}
      submitLabel="Continue"
      canSubmit={companyName.trim() !== '' && stage !== ''}
      submitting={false}
      error={null}
      onClose={onClose}
      onSubmit={() => {
        setConfirming(true);
      }}
    >
      <Alert severity="info" variant="outlined">
        CRM creates the client and generates its Client ID. The lead keeps the link.
      </Alert>
      <TextField
        label="Company name"
        required
        value={companyName}
        onChange={(e) => {
          setCompanyName(e.target.value);
        }}
      />
      <TextField
        select
        label="Lifecycle stage"
        required
        value={stage}
        onChange={(e) => {
          setStage(e.target.value);
        }}
      >
        {RELEASE_ONE_STAGES.map((value) => (
          <MenuItem key={value} value={value}>
            {STAGE_LABELS[value]}
          </MenuItem>
        ))}
      </TextField>
      {hasCapability(session.data, 'hr.employee:view') && (
        <ActiveEmployeePicker
          label="Account owner"
          value={owner}
          onChange={setOwner}
          helperText="Leave empty to make the lead's assigned employee the account owner."
        />
      )}
      <ListSelect
        label="Industry"
        values={crmLists.data?.industry}
        value={industryCode}
        current={lead.industryCode}
        onChange={setIndustryCode}
      />
      <ListSelect
        label="Client type"
        values={crmLists.data?.clientType}
        value={clientTypeCode}
        onChange={setClientTypeCode}
      />
      <ListSelect
        label="Client source"
        values={crmLists.data?.clientSource}
        value={sourceCode}
        onChange={setSourceCode}
      />
      <TextField
        label="Client email"
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
        }}
      />
      <TextField
        label="Client phone"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
        }}
      />
      {lead.contactName !== null && canAddContact && (
        <FormControlLabel
          control={
            <Checkbox
              checked={recordContact}
              onChange={(e) => {
                setRecordContact(e.target.checked);
              }}
            />
          }
          label={`Also record ${lead.contactName} as a client contact`}
        />
      )}
    </FormDialog>
  );
}

/** References a client that already exists instead of duplicating it (FRD-MKT-031). */
function LinkClientDialog({ lead, onClose }: { lead: LeadDto; onClose: () => void }) {
  const session = useSession();
  const changed = useLeadChanged();
  const [client, setClient] = useState<ClientDto | null>(null);
  const [confirming, setConfirming] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      postJson(
        `${LEADS}/${lead.id}/link-client`,
        { clientId: client?.id },
        LeadSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: lead.version },
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  if (confirming && client !== null) {
    return (
      <ConfirmDialog
        open
        title="Link to existing client"
        message={`Link lead ${lead.code} to ${client.code} — ${client.companyName}? The link cannot be changed afterwards.`}
        confirmLabel="Link"
        busy={save.isPending}
        error={save.isError ? save.error.message : null}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          save.mutate();
        }}
      />
    );
  }
  return (
    <FormDialog
      open
      title="Link to existing client"
      submitLabel="Continue"
      canSubmit={client !== null}
      submitting={false}
      error={null}
      onClose={onClose}
      onSubmit={() => {
        setConfirming(true);
      }}
    >
      <ActiveClientPicker
        label="Client"
        required
        value={client}
        onChange={setClient}
        helperText="The client this lead relates to. No new client is created."
      />
    </FormDialog>
  );
}
